import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FaCheckCircle, FaExclamationCircle, FaInfoCircle, FaTimes } from 'react-icons/fa';

const ToastContext = createContext(null);
let toastApi = null;

const toastStyles = {
  success: 'bg-green-500 text-white',
  error: 'bg-red-500 text-white',
  info: 'bg-primary-500 text-white',
  warning: 'bg-yellow-500 text-white',
};

const toastIcons = {
  success: FaCheckCircle,
  error: FaExclamationCircle,
  info: FaInfoCircle,
  warning: FaExclamationCircle,
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback((message, type = 'info', duration = 3500) => {
    const id = `${Date.now()}-${Math.random()}`;
    setToasts((prev) => [...prev, { id, message, type }]);

    if (duration > 0) {
      window.setTimeout(() => dismiss(id), duration);
    }

    return id;
  }, [dismiss]);

  const toast = useMemo(() => ({
    success: (message, duration) => showToast(message, 'success', duration),
    error: (message, duration) => showToast(message, 'error', duration),
    info: (message, duration) => showToast(message, 'info', duration),
    warning: (message, duration) => showToast(message, 'warning', duration),
    dismiss,
  }), [showToast, dismiss]);

  useEffect(() => {
    toastApi = toast;
    return () => {
      toastApi = null;
    };
  }, [toast]);

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div className="fixed top-16 right-4 z-[9999] flex w-full max-w-sm flex-col gap-2 pointer-events-none sm:top-[4.5rem] sm:right-5">
        {toasts.map((item) => {
          const Icon = toastIcons[item.type] || FaInfoCircle;
          return (
            <div
              key={item.id}
              className={`pointer-events-auto flex items-center gap-3 rounded-xl px-4 py-3 shadow-lg animate-slide-in-right ${toastStyles[item.type] || toastStyles.info}`}
              role="status"
            >
              <Icon className="h-5 w-5 shrink-0" />
              <p className="flex-1 text-sm font-medium">{item.message}</p>
              <button
                type="button"
                onClick={() => dismiss(item.id)}
                className="rounded-lg p-1 transition-colors hover:bg-white/20"
                aria-label="Dismiss notification"
              >
                <FaTimes className="h-3.5 w-3.5" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

export const toast = {
  success: (message, duration) => toastApi?.success(message, duration),
  error: (message, duration) => toastApi?.error(message, duration),
  info: (message, duration) => toastApi?.info(message, duration),
  warning: (message, duration) => toastApi?.warning(message, duration),
  dismiss: (id) => toastApi?.dismiss(id),
};
